import { ApolloClient } from '@apollo/client'
import { BackgroundJobType } from 'types/graphql'

import { navigate } from '@redwoodjs/router'

import { fetchViewUuidForInspectionsListNavigation } from 'src/fetch/views/fetchViewUuidForInspectionsListNavigation'
import { fetchViewUuidForRecordsListNavigation } from 'src/fetch/views/fetchViewUuidForRecordsListNavigation'
import { inspectionsListPathWithViewUuid } from 'src/fetch/views/inspectionsListViewUrl'
import { recordsListPathWithViewUuid } from 'src/fetch/views/recordsListViewUrl'

import { TrackedBackgroundJob } from './types'

type PillTrackableBackgroundJobType = 'BulkReassignRecords' | 'BulkReassignInspections'

type PillNavigationDestination = {
  ariaListName: string
  navigationErrorMessage: string
}

const pillNavigationDestinationByType: Record<
  PillTrackableBackgroundJobType,
  PillNavigationDestination
> = {
  BulkReassignInspections: {
    ariaListName: 'inspections list',
    navigationErrorMessage: 'Could not return to inspections view. Please try again.',
  },
  BulkReassignRecords: {
    ariaListName: 'records list',
    navigationErrorMessage: 'Could not return to records view. Please try again.',
  },
}

const isPillTrackableBackgroundJobType = (
  type: BackgroundJobType
): type is PillTrackableBackgroundJobType =>
  type === 'BulkReassignRecords' || type === 'BulkReassignInspections'

export const getBackgroundJobPillNavigationDestination = (
  type: BackgroundJobType
): PillNavigationDestination => {
  if (!isPillTrackableBackgroundJobType(type)) {
    throw new Error(`No pill navigation destination for background job type ${type}`)
  }

  return pillNavigationDestinationByType[type]
}

/** Terminal pill click: navigate staff to the module records list saved view they started from. */
export const navigateToOriginatingRecordsView = async ({
  apolloClient,
  job,
  organizationId,
  viewUuid: registeredViewUuid,
}: {
  apolloClient: ApolloClient<object>
  job: Pick<TrackedBackgroundJob, 'recordTypeId' | 'viewId'>
  organizationId: number
  /** When captured at job registration, skips the server lookup on pill click. */
  viewUuid?: string
}) => {
  const recordTypeId = job.recordTypeId
  if (recordTypeId == null) {
    throw new Error('Record type is required for records list pill navigation.')
  }

  const viewUuid =
    registeredViewUuid ??
    (await fetchViewUuidForRecordsListNavigation({
      apolloClient,
      organizationId,
      recordTypeId,
      viewId: job.viewId,
    }))

  if (!viewUuid) {
    throw new Error(`Could not resolve view uuid for viewId ${job.viewId}`)
  }

  navigate(
    recordsListPathWithViewUuid({
      recordTypeId,
      viewUuid,
    })
  )
}

/** Terminal pill click: navigate staff to the module inspections list saved view they started from. */
export const navigateToOriginatingInspectionsView = async ({
  apolloClient,
  job,
  organizationId,
  viewUuid: registeredViewUuid,
}: {
  apolloClient: ApolloClient<object>
  job: Pick<TrackedBackgroundJob, 'recordTypeId' | 'viewId'>
  organizationId: number
  viewUuid?: string
}) => {
  const recordTypeId = job.recordTypeId
  if (recordTypeId == null) {
    throw new Error('Record type is required for inspections list pill navigation.')
  }

  const viewUuid =
    registeredViewUuid ??
    (await fetchViewUuidForInspectionsListNavigation({
      apolloClient,
      organizationId,
      recordTypeId,
      viewId: job.viewId,
    }))

  if (!viewUuid) {
    throw new Error(`Could not resolve view uuid for viewId ${job.viewId}`)
  }

  navigate(
    inspectionsListPathWithViewUuid({
      recordTypeId,
      viewUuid,
    })
  )
}

/** Dispatches terminal pill navigation by job type. */
export const navigateToOriginatingView = async ({
  apolloClient,
  job,
  organizationId,
  viewUuid,
}: {
  apolloClient: ApolloClient<object>
  job: TrackedBackgroundJob
  organizationId: number
  viewUuid?: string
}) => {
  switch (job.type) {
    case 'BulkReassignInspections':
      await navigateToOriginatingInspectionsView({
        apolloClient,
        job,
        organizationId,
        viewUuid,
      })
      return
    case 'BulkReassignRecords':
      await navigateToOriginatingRecordsView({
        apolloClient,
        job,
        organizationId,
        viewUuid,
      })
      return
    default:
      throw new Error(`No pill navigation for background job type ${job.type}`)
  }
}
